/**
 * `useProjectTotals` — per-project income / expense / balance.
 *
 * Web-customer equivalent of the mobile hook
 * `src/features/transactions/useProjectTotals.ts`.
 *
 * Role-aware scope (mirrors mobile):
 *   - Submit-only roles (Site Engineer / Supervisor) must filter by
 *     `createdBy == uid` or the Firestore rule rejects the read. They
 *     end up with cashflow cells that reflect ONLY their own
 *     submissions on a project — which is the right semantic for
 *     "what have I personally submitted here".
 *   - Reader roles (SA / Admin / Manager / Accountant / Viewer) get
 *     the unscoped org query and the rules layer scopes to projects
 *     they can see.
 *   - Client (no canReadProjectTransactions) gets denied at the rule
 *     layer; we fall back to an empty Map and the UI shows "—".
 *
 * Totals semantics: only `posted` transactions count (legacy docs
 * without `workflowStatus` count too). `income` = sum of `payment_in`,
 * `expense` = sum of `payment_out`, balance = income − expense.
 * Legacy `'income'/'expense'` type values are normalised to the
 * canonical `payment_in`/`payment_out`.
 */
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { canSeeAnyTransactions, isSubmitOnlyTxnRole } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

export type ProjectTotals = {
  income: number;
  expense: number;
  balance: number;
};

export type UseProjectTotalsResult = {
  totalsByProject: Map<string, ProjectTotals>;
  loading: boolean;
  error: string | null;
};

type RawTransaction = {
  projectId?: string;
  type?: string;
  amount?: number;
  workflowStatus?: string;
};

function isCountedInTotals(t: RawTransaction): boolean {
  const w = t.workflowStatus;
  if (w == null) return true;
  return w === 'posted';
}

function normalizeType(raw: string | undefined): 'payment_in' | 'payment_out' | null {
  if (!raw) return null;
  if (raw === 'income' || raw === 'payment_in') return 'payment_in';
  if (raw === 'expense' || raw === 'payment_out') return 'payment_out';
  return null;
}

export function useProjectTotals(orgId: string | null): UseProjectTotalsResult {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  const [transactions, setTransactions] = useState<RawTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scopeToOwn = isSubmitOnlyTxnRole(orgRole);
  const hasAnyTxnAccess = canSeeAnyTransactions(orgRole);
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!orgId) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    // Roles with ZERO transaction access (Designer today) would fire
    // an unscoped org query the Firestore rule rejects in full. Skip
    // the listener — fall back to an empty Map so cashflow cells
    // show "—".
    if (!hasAnyTxnAccess) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    // Submit-only roles need uid to be known before we attach the
    // listener — without it the unscoped query gets rejected.
    if (scopeToOwn && !uid) {
      setTransactions([]);
      setLoading(true);
      return;
    }
    setLoading(true);
    setError(null);
    const constraints = [where('orgId', '==', orgId)];
    if (scopeToOwn && uid) {
      constraints.push(where('createdBy', '==', uid));
    }
    return onSnapshot(
      query(collection(db, 'transactions'), ...constraints),
      (snap) => {
        setTransactions(snap.docs.map((d) => d.data() as RawTransaction));
        setLoading(false);
      },
      (err) => {
        // Rules-denied (e.g. client viewer) falls through to an empty
        // Map — UI just shows "—" in the cashflow cells.
        console.warn('[useProjectTotals] snapshot error', err);
        setError(err.message);
        setTransactions([]);
        setLoading(false);
      },
    );
  }, [orgId, scopeToOwn, hasAnyTxnAccess, uid]);

  const totalsByProject = useMemo(() => {
    const m = new Map<string, ProjectTotals>();
    for (const t of transactions) {
      if (!t.projectId) continue;
      if (!isCountedInTotals(t)) continue;
      const kind = normalizeType(t.type);
      if (!kind) continue;
      const amount = typeof t.amount === 'number' ? t.amount : 0;
      const existing = m.get(t.projectId) ?? { income: 0, expense: 0, balance: 0 };
      if (kind === 'payment_in') existing.income += amount;
      else existing.expense += amount;
      existing.balance = existing.income - existing.expense;
      m.set(t.projectId, existing);
    }
    return m;
  }, [transactions]);

  return { totalsByProject, loading, error };
}
